import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiService } from "../../services/axios";

export const getExamQuestion = createAsyncThunk(
  "finalExam/startFinalTest",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.post("/student/startExam", params);

      return { ...response.data, questionNumber: params.question_number };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveExamAnswer = createAsyncThunk(
  "finalExam/saveAnswer",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.post(
        "/student/submit-answer",
        params,
        { skipErrorToast: true }
      );

      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markForReviewExamQuestion = createAsyncThunk(
  "finalExam/markForReview",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.post(
        "/student/markForReviewExam",
        params
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const endFinalExam = createAsyncThunk(
  "finalExam/endExam",
  async (params, { rejectWithValue }) => {
    try {
      const requestData = {
        examAttemptId: params?.examId,
      };
      const response = await apiService.post(
        "/student/end_exam_and_results",
        requestData
      );
      // apiService.post returns the full Axios response
      const body = response?.data ?? response;
      return body;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const reviewFinalExam = createAsyncThunk(
  "finalExam/reviewExam",
  async (params, { rejectWithValue }) => {
    try {
      const requestData = {
        examAttemptId: params?.examId,
        question_number: params?.questionNumber,
      };
      const response = await apiService.post(
        "/student/review-exam",
        requestData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  examId: null,
  questionsList: {},
  currentQuestionNumber: 1,
  loading: false,
  error: null,
  stats: {
    validAnswer: [],
    inValidAnswer: [],
    notAnswered: 0,
    markedForReview: [],
    totalQuestions: 0,
  },
  isCompleted: false,
  finalExamResult: [],
  reviewExam: {},
  questionsList: {},
  examData: null,
  hasPartB: false,
  drawingQuestionCount: 0,
  questionPaperId: null,
  questionTypes: [],
};

const finalExamSlice = createSlice({
  name: "finalExams",
  initialState,
  reducers: {
    setCurrentQuestionIndex: (state, action) => {
      state.currentQuestionNumber = action.payload;
    },
    setCurrentQuestionAnswer: (state, action) => {
      state.questionsList[action.payload.questionNumber] = {
        ...state.questionsList[action.payload.questionNumber],
        selectedAnswer: action.payload.answer,
      };
    },
    resetFinalExam: (state) => {
      state.examId = null;
      state.questionsList = {};
      state.currentQuestionNumber = 1;
      state.loading = false;
      state.error = null;
      state.stats = {
        validAnswer: [],
        inValidAnswer: [],
        notAnswered: 0,
        markedForReview: [],
        totalQuestions: 0,
      };
      state.isCompleted = false;
      state.finalExamResult = [];
      state.reviewExam = {};
      state.hasPartB = false;
      state.drawingQuestionCount = 0;
      state.questionPaperId = null;
      state.questionTypes = [];
    },
    setExamData: (state, action) => {
      state.examData = action.payload;
      const drawingCount = action.payload?.drawingQuestionCount || 0;
      state.hasPartB = drawingCount > 0;
      state.drawingQuestionCount = drawingCount;
      if (action.payload?.id) {
        state.questionPaperId = action.payload.id;
      }
    },
    clearExamData: (state) => {
      state.examData = {};
      state.hasPartB = false;
      state.drawingQuestionCount = 0;
    },
    clearExamCompleted: (state) => {
      state.isCompleted = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExamQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getExamQuestion.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.examId = payload.examId;
        if (payload.hasPartB !== undefined) {
          state.hasPartB = !!payload.hasPartB;
        }
        if (payload.drawingQuestionCount !== undefined) {
          state.drawingQuestionCount = payload.drawingQuestionCount || 0;
        }
        if (payload.questionPaperId) {
          state.questionPaperId = payload.questionPaperId;
        }
        if (Array.isArray(payload.questionTypes)) {
          state.questionTypes = payload.questionTypes;
        }

        const question = {
          questionNumber: payload.questionNumber,
          questionText: payload.question || "",
          options: payload.options || [],
          point: payload.point || 0,
          questionType: payload.questionType,
          point: payload?.point,
          userAnswer: payload?.answer,
          totalQuestions: payload?.total_question,
          totalMarks: payload?.total_marks,
          result: "Not Answered",
          markReview: false,
        };

        if (!state.questionsList) {
          state.questionsList = {};
        }
        state.questionsList[question.questionNumber] = {
          ...state.questionsList[question.questionNumber],
          ...question,
        };

        state.stats = payload.statusCounts;
        state.stats.markedForReview=payload.statusCounts.marked_review

        state.currentQuestionNumber = question.questionNumber;
      })
      .addCase(getExamQuestion.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message || "Failed to get exam question";
      })
      .addCase(saveExamAnswer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveExamAnswer.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload.data.question) {
          state.questionsList[payload.data.question.questionNumber] = {
            ...state.questionsList[payload.data.question.questionNumber],
            ...payload.data.question,
            result: payload.data.question.isCorrect ? "Correct" : "Incorrect",
          };
        }

        state.stats = { ...state.stats, ...payload.data.statusCounts };
      })
      .addCase(saveExamAnswer.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message || "Failed to save answer";
      })
      .addCase(markForReviewExamQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markForReviewExamQuestion.fulfilled, (state, { payload }) => {
        state.loading = false;

        if (payload.question) {
          state.questionsList[payload.question.questionNumber] = {
            ...state.questionsList[payload.question.questionNumber],
            markReview: payload.question.markReview,
          };
        }

        if (payload.meta) {
          state.stats = {
            ...state.stats,
            markedForReview:
              payload.meta.markForReviewQue || state.stats.markedForReview,
          };
        }
      })
      .addCase(markForReviewExamQuestion.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message;
      })
      .addCase(endFinalExam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(endFinalExam.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.isCompleted = true;
        state.finalExamResult = payload;
      })
      .addCase(endFinalExam.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message || "Failed to end exam";
      })
      .addCase(reviewFinalExam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reviewFinalExam.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.examId = payload?._id;
        // Use this attempt's types only, so headings from another exam never show.
        state.questionTypes = Array.isArray(payload?.questionTypes)
          ? payload.questionTypes
          : [];
        const question = {
          questionNumber: payload?.question?.question_number || 1,
          questionText: payload?.question?.questionText || "",
          options:
            payload.question?.options?.map((item) => {
              return { ...item, id: item?._id };
            }) || [],
          point: payload.question?.points || 0,
          questionType: payload.question?.questionType,
          selectedAnswer: payload?.question?.selectedAnswer,
          correctAnswer: payload?.question?.correctAnswer?.[0]?.answer,
          solution: payload?.question?.solution,
          isCorrect: payload?.question?.isCorrect,
          correctAnswersCount: payload?.correctAnswers?.length,
          incorrectAnswersCount: payload?.incorrectAnswers?.length,
          notAnsweredCount: payload?.notAnswered?.length,
          totalQuestions: payload?.totalQuestions,
          markReview: false,
        };
        if (!state.questionsList) {
          state.questionsList = {};
        }
        if (!state.stats) {
          state.stats = {};
        }
        state.questionsList[question.questionNumber] = {
          ...state.questionsList[question.questionNumber],
          ...question,
        };

        state.stats.totalQuestions = payload.totalQuestions;
        state.stats.validAnswer = payload.correctAnswers;
        state.stats.inValidAnswer = payload.incorrectAnswers;
        state.currentQuestionNumber = question.questionNumber;
      })
      .addCase(reviewFinalExam.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message || "Failed to end exam";
      });
  },
});

export const {
  setCurrentQuestionIndex,
  setCurrentQuestionAnswer,
  resetFinalExam,
  setExamData,
  clearExamData,
  clearExamCompleted,
} = finalExamSlice.actions;

export default finalExamSlice.reducer;

// import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import { apiService } from "../../services/axios";

// export const getExamQuestion = createAsyncThunk(
//   "finalExam/startFinalTest",
//   async (params, { rejectWithValue }) => {
//     try {
//       const requestData = {
//         questionPaperId: params.id,
//         question_number: params.questionNumber || 1,
//       };

//       const response = await apiService.post("/student/startExam", requestData);
//       return response.data;
//     } catch (error) {
//       return rejectWithValue(error.response?.data || error.message);
//     }
//   }
// );

// const initialState = {
//   mockTestId: null,
//   questionsList: {},
//   currentQuestionNumber: 1,
//   loading: false,
//   error: null,
//   stats: {
//     validAnswer: [],
//     inValidAnswer: [],
//     notAnswered: 0,
//     markedForReview: [],
//     totalQuestions: 0,
//   },
//   isCompleted: false,
// };

// const finalExamSlice = createSlice({
//   name: "exams",
//   initialState,
//   reducers: {
//     setCurrentQuestionIndex: (state, action) => {
//       state.currentQuestionNumber = action.payload;
//     },
//     setCurrentQuestionAnswer: (state, action) => {
//       state.questionsList[action.payload.questionNumber] = {
//         ...state.questionsList[action.payload.questionNumber],
//         selectedAnswer: action.payload.answer,
//       };
//     },
//     resetMockTest: () => initialState,
//   },
//   extraReducers: (builder) => {
//     builder
//       .addCase(getExamQuestion.pending, (state) => {
//         state.error = null;
//       })
//       .addCase(getExamQuestion.fulfilled, (state, { payload }) => {
//         state.mockTestId = payload.examId;
//         const { question, meta, totalQuestions } = payload;
//         state.questionsList[question.questionNumber] = {
//           ...state.questionsList[question.questionNumber],
//           ...question,
//         };
//         state.stats.totalQuestions =
//           totalQuestions || state.stats.totalQuestions;
//         if (meta) {
//           state.stats = {
//             ...state.stats,
//             inValidAnswer: meta.invalidQue || state.stats.inValidAnswer,
//             markedForReview:
//               meta.markForReviewQue || state.stats.markedForReview,
//             notAnswered: meta.notAns || state.stats.notAnswered,
//             validAnswer: meta.validQue || state.stats.validAnswer,
//           };
//         }
//         state.currentQuestionNumber = question.questionNumber;
//       })
//       .addCase(getExamQuestion.rejected, (state, { payload }) => {
//         state.error = payload?.message || "Failed to start mock test";
//       })
//   },
// });

// export const {
//   setCurrentQuestionIndex,
//   setCurrentQuestionAnswer,
//   resetMockTest,
// } = finalExamSlice.actions;

// export default finalExamSlice.reducer;
